import React, { useEffect, useRef } from 'react';
import { Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import useDesignerStore from './designerStore';

const ImageShape = ({ obj, onSelect, onUpdate, isSnap, grid, refCallback }) => {
  const imageRef = useRef();
  const isSilverSurface = useDesignerStore(
    s => s.activeSurface === 'frontSilver' || s.activeSurface === 'backSilver');
  const imageFilters = isSilverSurface
    ? [Konva.Filters.Grayscale] // 🔒 silver pipeline only
    : [
        obj.brightness !== 0 ? Konva.Filters.Brighten : null,
        obj.contrast !== 0 ? Konva.Filters.Contrast : null,
        obj.grayscale ? Konva.Filters.Grayscale : null,
        obj.sepia ? Konva.Filters.Sepia : null
      ].filter(Boolean);
  const [image] = useImage(obj.originalSrc, 'anonymous');


  useEffect(() => {
    if (!imageRef.current || !image) return;

    imageRef.current.clearCache();
    imageRef.current.cache();
    imageRef.current.getLayer()?.batchDraw();
  }, [
    image,
    isSilverSurface,
    imageFilters,
    obj.width,
    obj.height,
    obj.opacity,
    obj.brightness,
    obj.contrast,
    obj.grayscale,
    obj.stroke,
    obj.strokeWidth,
    obj.cornerRadius,
    obj.dash,
    obj.sepia
  ]);



  // Determine position and dimensions
  let x = obj.x;
  let y = obj.y;
  let width = obj.width;
  let height = obj.height;

  const container = obj.containerId
    ? useDesignerStore.getState().objects.find(o => o.id === obj.containerId)
    : null;

  if (container) {
    x = container.x;
    y = container.y;
    width = container.width;
    height = container.height;
  }

  let crop = undefined;
  if (image && obj.fitMode === 'cover') {
    const imgAspect = image.width / image.height;
    const boxAspect = width / height;

    let srcWidth, srcHeight, offsetX, offsetY;

    if (imgAspect > boxAspect) {
      srcHeight = image.height;
      srcWidth = srcHeight * boxAspect;
      offsetX = (image.width - srcWidth) / 2;
      offsetY = 0;
    } else {
      srcWidth = image.width;
      srcHeight = srcWidth / boxAspect;
      offsetX = 0;
      offsetY = (image.height - srcHeight) / 2;
    }

    crop = { x: offsetX, y: offsetY, width: srcWidth, height: srcHeight };
  }

  if (!image) {
    return null;
  }
    return (
      <KonvaImage
        key={`${obj.id}-${obj.originalSrc}-${isSilverSurface}`}
        id={obj.id}
        ref={(node) => {
          imageRef.current = node;
          if (refCallback) refCallback(node);
        }}
        image={image}
        x={x}
        y={y}
        width={width}
        height={height}
        crop={crop}
        draggable={!obj.useAsBackground}
        rotation={obj.rotation || 0}
        stroke={obj.stroke}
        strokeWidth={obj.strokeWidth}
        cornerRadius={obj.cornerRadius}
        dash={obj.dash || []}
        filters={imageFilters}
        opacity={
          isSilverSurface
            ? (obj.density ?? 1.0)   // silver strength
            : (obj.opacity ?? 1)     // 👈 use real opacity
        }
        brightness={obj.brightness ?? 0}
        contrast={obj.contrast ?? 0}
        onClick={(e) => onSelect(e, obj.id)}
        onDragEnd={(e) => {
          const rawX = e.target.x();
          const rawY = e.target.y();
          const snappedX = isSnap ? Math.round(rawX / grid) * grid : rawX;
          const snappedY = isSnap ? Math.round(rawY / grid) * grid : rawY;
          onUpdate(obj.id, { x: snappedX, y: snappedY });
        }}
        onTransformEnd={(e) => {
          const node = e.target;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          onUpdate(obj.id, {
            x: node.x(),
            y: node.y(),
            width: node.width() * scaleX,
            height: node.height() * scaleY,
            rotation: Math.round(node.rotation() * 100) / 100
          });
        }}
      />
    );
  };

export default ImageShape;
